// src/pages/NotFound.jsx
import React, { useContext } from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useNavigate, Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContextProvider";
import HomeIcon from "@mui/icons-material/Home";
import spandanLogo from "../assets/images/logos/spandan-logo.svg";

const PRIMARY = "#5751E9";

export default function NotFound() {
  const navigate = useNavigate();
  const { is_login } = useContext(AuthContext);

  // Redirect if not logged in
  if (!is_login) return <Navigate to="/loginpassword" replace />;

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "85vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        /* slide up + fade animation for the card */
        sx={{
          width: "100%",
          p: 4,
          borderRadius: 4,
          background: "var(--app-surface)",
          boxShadow: "var(--app-shadow)",
          // keyframes and animation
          "@keyframes slideUpFade": {
            "0%": { transform: "translateY(18px)", opacity: 0 },
            "100%": { transform: "translateY(0)", opacity: 1 },
          },
          animation: "slideUpFade 420ms cubic-bezier(.2,.9,.2,1) forwards",
        }}
      >
        {/* Logo (pop animation) */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            // logo pop
            "@keyframes logoPop": {
              "0%": { transform: "scale(0.85)", opacity: 0 },
              "60%": { transform: "scale(1.06)", opacity: 1 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
            animation: "logoPop 420ms ease-out",
          }}
        >
          <img
            src={spandanLogo}
            alt="Spandan Logo"
            style={{
              width: "120px",
              height: "auto",
              opacity: 0.96,
            }}
          />
        </Box>

        {/* Floating SVG */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "240px",
            mx: "auto",
            mb: 2,
            // float animation
            "@keyframes floatY": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-8px)" },
              "100%": { transform: "translateY(0px)" },
            },
            animation: "floatY 3.6s ease-in-out infinite",
            // subtle drop shadow for depth
            filter: "drop-shadow(0px 8px 22px rgba(87,81,233,0.06))",
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 400 320"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            role="img"
          >
            <g fill="none" fillRule="evenodd">
              <circle cx="200" cy="140" r="90" fill={PRIMARY} opacity="0.08" />
              <text
                x="130"
                y="170"
                fontFamily="Inter, Roboto, Arial"
                fontSize="80"
                fontWeight="800"
                fill={PRIMARY}
              >
                404
              </text>
            </g>
          </svg>
        </Box>

        {/* Headings (centered) */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}
        >
          Page Not Found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center", maxWidth: "88%", mx: "auto" }}
        >
          The page you’re looking for does not exist or may have been moved. Use
          the button below to go back to the homepage or check the URL.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            startIcon={<HomeIcon />}
            sx={{
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: "#453FD3" },
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            Go to Homepage
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3, textAlign: "center" }}
        >
          Tip: Make sure the URL is correct or return to your dashboard.
        </Typography>
      </Box>
    </Container>
  );
}
